import { Check, User, Users, X } from 'lucide-react';
import type { ChatProposalView } from '../../hooks/useChatSession';

interface ApprovalCardProps {
  proposals: ChatProposalView[];
  /** Absent while the decision is pending; set once decided/superseded. */
  resolution?: 'approved' | 'rejected' | 'superseded' | null;
  /** Present (and enabled) only while pending. */
  onRespond?: (approve: boolean) => void;
  deciding?: boolean;
}

const TOOL_LABELS: Record<string, string> = {
  write_doc: 'Save document',
  append_to_doc: 'Add to document',
};

/**
 * The write-proposal card: shows each proposed document write with its
 * DESTINATION badge ("Personal" vs the space name — resolved server-side, the
 * user-visible backstop against a guessed space slug landing in the personal
 * tree), the exact path, and the full proposed content. One Approve/Decline
 * decision covers the batch; resolved cards show the outcome instead.
 */
export function ApprovalCard({ proposals, resolution, onRespond, deciding }: ApprovalCardProps) {
  const pending = !resolution;

  return (
    <div
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-2)] p-4"
      role="group"
      aria-label={pending ? 'Proposed update awaiting your approval' : 'Proposed update'}
    >
      <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">
        {pending ? "I'd like to save" : 'I proposed'}
      </p>

      <ul className="mt-2 space-y-3 list-none">
        {proposals.map((proposal) => {
          const isSpace = Boolean(proposal.spaceId);
          const DestinationIcon = isSpace ? Users : User;
          return (
            <li key={proposal.id} className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                    isSpace
                      ? 'bg-[color-mix(in_oklab,var(--marine-400)_18%,transparent)] text-[var(--marine-700)]'
                      : 'bg-[color-mix(in_oklab,var(--text-primary)_8%,transparent)] text-[var(--text-secondary)]'
                  }`}
                >
                  <DestinationIcon className="w-3 h-3" aria-hidden />
                  {proposal.treeLabel}
                </span>
                <span className="text-xs text-[var(--text-tertiary)]">
                  {TOOL_LABELS[proposal.tool] ?? proposal.tool}
                </span>
              </div>
              <p
                className="mt-1 font-mono text-xs text-[var(--text-primary)] break-all"
                title={proposal.path}
              >
                {proposal.path}
              </p>
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3 text-xs whitespace-pre-wrap break-words text-[var(--text-secondary)]">
                {proposal.content}
              </pre>
              {resolution === 'approved' && proposal.error && (
                <p role="alert" className="mt-1.5 text-xs text-[var(--danger)] break-words">
                  Could not save: {proposal.error}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {pending && onRespond ? (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            className="btn btn-primary"
            disabled={deciding}
            onClick={() => onRespond(true)}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={deciding}
            onClick={() => onRespond(false)}
          >
            Decline
          </button>
        </div>
      ) : (
        !pending && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-[var(--text-tertiary)]">
            {resolution === 'approved' ? (
              <>
                <Check className="w-4 h-4 text-[var(--energy-nourishing)]" aria-hidden />
                Saved
              </>
            ) : resolution === 'rejected' ? (
              <>
                <X className="w-4 h-4" aria-hidden />
                Declined
              </>
            ) : (
              <>
                <X className="w-4 h-4" aria-hidden />
                Not saved — you moved on
              </>
            )}
          </p>
        )
      )}
    </div>
  );
}
